'use client'

import React, { useState, useRef, useEffect } from 'react'
import Link from 'next/link'
import { ArrowUpRight, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import type { VisaType } from '@/payload-types'

interface VisaTypesCarouselProps {
  visaTypes: VisaType[]
}

export function VisaTypesCarousel({ visaTypes }: VisaTypesCarouselProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isAtEnd, setIsAtEnd] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const filteredVisaTypes = searchQuery
    ? visaTypes.filter(
        (visa) =>
          visa.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          visa.shortDescription?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : visaTypes

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      // Check if scrolled to the end (with 5px tolerance)
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 5)
    }
  }

  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) {
      // Check initial position
      checkScrollPosition()
      // Add scroll listener
      container.addEventListener('scroll', checkScrollPosition)
      return () => container.removeEventListener('scroll', checkScrollPosition)
    }
  }, [filteredVisaTypes])

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' })
    }
  }

  return (
    <section className="py-8 md:py-8 md:pb-12 px-6 md:px-12">
      <div className="max-w-380 mx-auto">
        {/* Header with Search */}
        <div className="flex flex-col md:flex-row items-center md:items-center justify-between gap-4 mb-8">
          <Link
            href="/visas"
            className="inline-flex items-center gap-2 text-xl md:text-2xl font-medium text-gray-900 hover:underline underline-offset-8 mt-4"
          >
            Explore all visa pathways
            <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6" />
          </Link>

          {/* Search Bar */}
          <div className="relative w-full md:w-auto md:min-w-[320px]">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <Search className="w-4 h-4 text-white" />
              </div>
            </div>
            <input
              type="text"
              placeholder="Search by visa type"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-14 pr-4 py-3 rounded-full border border-gray-300 bg-white text-gray-900 placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
            />
          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="relative">
            {/* Fade gradient overlay on the right */}
            <div
              className={`absolute right-0 top-0 bottom-4 w-12 md:w-16 pointer-events-none z-10 transition-opacity duration-300 ${
                isAtEnd ? 'opacity-0' : 'opacity-100'
              }`}
              style={{
                background: 'linear-gradient(to left, rgba(245, 245, 240, 0.7), transparent)',
              }}
            />

            <div
              ref={scrollContainerRef}
              className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filteredVisaTypes.map((visaType) => (
                <Link
                  key={visaType.id}
                  href={`/visas/${visaType.slug}`}
                  className="shrink-0 w-[280px] md:w-[320px] bg-white rounded-2xl p-6 md:p-8 md:py-6 border border-primary/30 hover:border-primary transition-colors snap-start group"
                >
                  <h3 className="text-xl font-semibold text-brand-gold mb-3 md:mb-2 line-clamp-1">
                    {visaType.name}
                  </h3>
                  <p className="text-sm md:text-base text-gray-700 mb-2 md:mb-4 line-clamp-4">
                    {visaType.shortDescription}
                  </p>
                  <div className="inline-flex items-center gap-2 text-sm md:text-base font-semibold text-primary transition-colors">
                    Learn More
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="flex items-center gap-2 mt-6">
            <button
              onClick={scrollLeft}
              className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <button
              onClick={scrollRight}
              className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>

        {/* No results message */}
        {filteredVisaTypes.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No visa types match your search.</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  )
}
